import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClipboard, FiClock, FiCheckCircle, FiSearch, FiX, FiEye, FiAlertCircle } from 'react-icons/fi';
import useAPI from '../../hooks/useAPI';
import endpointsAdmin from '../../Services/endpointAdmin';
import api, { buildFileUrl } from '../../Services/api';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  getAdminLembagaScope,
  matchItemToAdminScope,
} from './adminScope';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '-';

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function resolveStatus(status) {
  const statusMap = {
    pending: { label: 'Menunggu', color: 'yellow' },
    verifikasi: { label: 'Verifikasi', color: 'blue' },
    disetujui: { label: 'Disetujui', color: 'green' },
    approved: { label: 'Disetujui', color: 'green' },
    ditolak: { label: 'Ditolak', color: 'red' },
    rejected: { label: 'Ditolak', color: 'red' },
    selesai: { label: 'Selesai', color: 'blue' },
    completed: { label: 'Selesai', color: 'blue' },
  };

  const normalized = normalizeStatus(status);
  return statusMap[normalized] || { label: String(status || 'Belum ada riwayat'), color: 'gray' };
}

function normalizeStatus(status) {
  return String(status || '').toLowerCase().trim();
}

function normalizeId(value) {
  return String(value ?? '').trim();
}

function getDataPengajuanId(item) {
  return normalizeId(item?.id || item?.id_data_pengajuan || item?.dataPengajuan?.id);
}

function getRiwayatDataPengajuanId(item) {
  return normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id);
}

function getProgramName(item) {
  return item?.program?.nama_program || item?.nama_program || '-';
}

function getLembagaName(item) {
  return item?.lembaga?.nama_lembaga || item?.nama_lembaga || '-';
}

function getFileUrl(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (rawValue.startsWith('data:image/')) return rawValue;
  return buildFileUrl(rawValue);
}

function DetailItem({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 transition hover:bg-white hover:shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <div className="mt-2 text-sm font-semibold leading-6 text-slate-800">{value || '-'}</div>
    </div>
  );
}

function MediaGrid({ title, items, getTitle, getSubtitle }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white px-5 py-4">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
          {items.length} file
        </span>
      </div>

      <div className="p-5">
        {items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
            Belum ada data.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item, index) => {
              const fileUrl = getFileUrl(item?.file_path);

              return (
                <div key={item?.id_dokumen || item?.id_foto || `${title}-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition hover:shadow-md">
                  {fileUrl ? (
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="block bg-white">
                      <img
                        src={fileUrl}
                        alt={getTitle(item)}
                        className="h-44 w-full object-contain"
                      />
                    </a>
                  ) : (
                    <div className="flex h-44 items-center justify-center bg-white text-sm text-slate-400">
                      File tidak tersedia
                    </div>
                  )}
                  <div className="space-y-1 p-4">
                    <p className="text-sm font-semibold text-slate-900">{getTitle(item)}</p>
                    <p className="text-xs leading-5 text-slate-500">{getSubtitle(item)}</p>
                    {fileUrl ? (
                      <a href={fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900">
                        <FiEye className="h-3 w-3" /> Buka file
                      </a>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function ListPengajuanAdmin({ admin }) {
  const { data: dataPengajuanData, loading: loadingDataPengajuan, error: dataPengajuanError } = useAPI(endpointsAdmin.data_pengajuan.getAll);
  const { data: dokumenData, loading: loadingDokumen } = useAPI(endpointsAdmin.dokumen_pengajuan.getAll);
  const { data: riwayatData, loading: loadingRiwayat } = useAPI(endpointsAdmin.riwayat_pengajuan.getAll);
  const { data: jenisFotoData, loading: loadingJenisFoto } = useAPI(endpointsAdmin.jenis_foto.getAll);
  const { data: tblFotoData, loading: loadingTblFoto } = useAPI(endpointsAdmin.tbl_foto.getAll);

  const [adminMitraRecords, setAdminMitraRecords] = useState([]);
  const [loadingAdminMitra, setLoadingAdminMitra] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPengajuan, setSelectedPengajuan] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  const lembagaScope = getAdminLembagaScope(admin);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        setLoadingAdminMitra(true);
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
      } catch (fetchError) {
        if (cancelled) return;
        setAdminMitraRecords([]);
      } finally {
        if (!cancelled) {
          setLoadingAdminMitra(false);
        }
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  const dataPengajuanList = useMemo(
    () => extractArrayPayload(dataPengajuanData, ['data_pengajuan', 'dataPengajuan', 'pengajuan', 'data']),
    [dataPengajuanData]
  );

  const dokumenList = useMemo(
    () => extractArrayPayload(dokumenData, ['dokumen_pengajuan', 'dokumenPengajuan', 'dokumen', 'data']),
    [dokumenData]
  );

  const riwayatList = useMemo(
    () => extractArrayPayload(riwayatData, ['riwayat_pengajuan', 'riwayatPengajuan', 'pengajuan', 'data']),
    [riwayatData]
  );

  const jenisFotoList = useMemo(
    () => extractArrayPayload(jenisFotoData, ['jenis_foto', 'jenisFoto', 'data']),
    [jenisFotoData]
  );

  const tblFotoList = useMemo(
    () => extractArrayPayload(tblFotoData, ['foto_pengajuan', 'tbl_foto', 'tblFoto', 'foto', 'data']),
    [tblFotoData]
  );

  const riwayatByDataId = useMemo(() => {
    const grouped = new Map();

    riwayatList.forEach((item) => {
      const id = getRiwayatDataPengajuanId(item);
      if (!id) return;

      const previous = grouped.get(id);
      const previousDate = new Date(previous?.created_at || previous?.createdAt || 0).getTime();
      const itemDate = new Date(item?.created_at || item?.createdAt || 0).getTime();

      if (!previous || itemDate >= previousDate) {
        grouped.set(id, item);
      }
    });

    return grouped;
  }, [riwayatList]);

  const detailByDataId = useMemo(() => {
    const dokumenById = new Map();
    const fotoById = new Map();
    const riwayatGroupById = new Map();

    dokumenList.forEach((item) => {
      const id = normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id);
      if (!id) return;
      dokumenById.set(id, [...(dokumenById.get(id) || []), item]);
    });

    tblFotoList.forEach((item) => {
      const id = normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id);
      if (!id) return;
      fotoById.set(id, [...(fotoById.get(id) || []), item]);
    });

    riwayatList.forEach((item) => {
      const id = getRiwayatDataPengajuanId(item);
      if (!id) return;
      riwayatGroupById.set(id, [...(riwayatGroupById.get(id) || []), item]);
    });

    return { dokumenById, fotoById, riwayatGroupById };
  }, [dokumenList, tblFotoList, riwayatList]);

  const pengajuanList = useMemo(() => {
    return dataPengajuanList
      .map((dataPengajuan) => {
        const dataId = getDataPengajuanId(dataPengajuan);
        const latestRiwayat = riwayatByDataId.get(dataId) || null;

        return {
          ...dataPengajuan,
          latestRiwayat,
          program: latestRiwayat?.program || dataPengajuan?.program || null,
          lembaga: latestRiwayat?.lembaga || dataPengajuan?.lembaga || null,
          status: latestRiwayat?.status || dataPengajuan?.status || '',
          sumber: latestRiwayat?.sumber || dataPengajuan?.sumber || '-',
        };
      })
      .filter((item) => {
        if (!lembagaScope.active) return true;

        const matchesData = matchItemToAdminScope(item, lembagaScope, adminMitraScope);
        const matchesRiwayat = item.latestRiwayat ? matchItemToAdminScope(item.latestRiwayat, lembagaScope, adminMitraScope) : false;

        return matchesData || matchesRiwayat;
      });
  }, [dataPengajuanList, riwayatByDataId, lembagaScope, adminMitraScope]);

  const filteredPengajuan = useMemo(() => {
    if (!searchTerm.trim()) return pengajuanList;

    const query = searchTerm.toLowerCase();
    return pengajuanList.filter((item) => {
      const haystack = [
        item?.id,
        item?.namaLengkap,
        item?.nik,
        item?.noKk,
        item?.noHp,
        item?.kabupatenKota,
        item?.kecamatan,
        getProgramName(item),
        getLembagaName(item),
        item?.status,
      ].join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [pengajuanList, searchTerm]);

  const selectedDetail = useMemo(() => {
    if (!selectedPengajuan) {
      return { dokumen: [], foto: [], riwayat: [] };
    }

    const dataId = getDataPengajuanId(selectedPengajuan);
    const dokumen = detailByDataId.dokumenById.get(dataId) || [];
    const foto = detailByDataId.fotoById.get(dataId) || [];
    const riwayat = detailByDataId.riwayatGroupById.get(dataId) || [];

    return {
      dokumen,
      foto,
      riwayat: [...riwayat].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)),
    };
  }, [selectedPengajuan, detailByDataId]);

  const jenisFotoById = useMemo(() => {
    const map = new Map();
    jenisFotoList.forEach((item) => {
      map.set(normalizeId(item?.id_jenis_foto || item?.id), item);
    });
    return map;
  }, [jenisFotoList]);

  const navigate = useNavigate();

  const handleOpenDetail = (pengajuan) => {
    const dataId = getDataPengajuanId(pengajuan);
    if (!dataId) return;
    navigate(`/pengajuan/riwayat/${dataId}`);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedPengajuan(null);
  };

  const handleGenerateLink = () => {
    if (lembagaScope?.id) {
      setGeneratedLink(`https://pm.mpzdt.my.id/?id_lembaga=${lembagaScope.id}`);
    } else {
      setGeneratedLink('https://pm.mpzdt.my.id/');
    }
    setCopiedLink(false);
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopiedLink(true);
    } catch (error) {
      setCopiedLink(false);
    }
  };

  const loading =
    loadingDataPengajuan ||
    loadingDokumen ||
    loadingRiwayat ||
    loadingJenisFoto ||
    loadingTblFoto ||
    loadingAdminMitra;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12 lg:px-16">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="relative mx-auto mb-5 w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
              <div className="animate-spin absolute inset-0 rounded-full border-4 border-blue-900 border-t-transparent"></div>
            </div>
            <p className="text-slate-700 font-semibold">Memuat data pengajuan...</p>
            <p className="text-sm text-slate-400 mt-1">Mohon tunggu sebentar</p>
          </div>
        </div>
      </div>
    );
  }

  if (dataPengajuanError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12 lg:px-16">
        <div className="flex items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FiAlertCircle className="text-red-500 h-5 w-5" />
          </div>
          <div>
            <p className="text-red-700 font-semibold">Terjadi Kesalahan</p>
            <p className="text-red-500 text-sm mt-1">{dataPengajuanError}</p>
          </div>
        </div>
      </div>
    );
  }

  const colorMap = {
    yellow: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    green: 'bg-green-50 text-green-700 border border-green-200',
    red: 'bg-red-50 text-red-700 border border-red-200',
    blue: 'bg-blue-50 text-blue-700 border border-blue-200',
    gray: 'bg-slate-50 text-slate-700 border border-slate-200',
  };

  const totalData = filteredPengajuan.length;
  const totalMenunggu = filteredPengajuan.filter((p) => normalizeStatus(p?.status) === 'pending').length;
  const totalDisetujui = filteredPengajuan.filter((p) => ['disetujui', 'approved'].includes(normalizeStatus(p?.status))).length;
  const totalSelesai = filteredPengajuan.filter((p) => ['selesai', 'completed'].includes(normalizeStatus(p?.status))).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="w-full space-y-7 px-4 py-8 sm:px-6 lg:px-10 xl:px-16">

        {/* ── HEADER ── */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />
          <div className="flex flex-col gap-5 px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/30 flex-shrink-0">
                <FiClipboard className="text-white text-xl" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Pengajuan</p>
                <h1 className="mt-0.5 text-2xl font-bold leading-tight text-slate-900 lg:text-3xl">Data Pengajuan</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Tabel utama data pengajuan. Dokumen, foto, dan riwayat dapat dilihat dari detail.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleGenerateLink}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-bold text-blue-700 shadow-sm transition hover:bg-blue-100"
              >
                <FiClipboard className="h-3.5 w-3.5" />
                Generate Link Pengajuan
              </button>
              <span className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-700 shadow-sm">
                <FiClock className="h-3.5 w-3.5" />
                {totalMenunggu} menunggu
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs font-bold text-emerald-700 shadow-sm">
                <FiCheckCircle className="h-3.5 w-3.5" />
                {totalDisetujui} disetujui
              </span>
            </div>
          </div>
        </section>

        {generatedLink && (
          <div className="flex flex-col justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 sm:flex-row sm:items-center">
            <div>
              <span className="font-bold">Link Berhasil Dibuat:</span>{' '}
              <a href={generatedLink} target="_blank" rel="noopener noreferrer" className="break-all underline hover:text-emerald-900">
                {generatedLink}
              </a>
            </div>
            <button
              type="button"
              onClick={handleCopyLink}
              className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-bold transition ${copiedLink ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-emerald-200 text-emerald-800 hover:bg-emerald-300'}`}
            >
              {copiedLink ? '✓ Tersalin' : 'Copy Link'}
            </button>
          </div>
        )}

        {/* ── METRIC CARDS ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { title: 'Total Data', value: totalData, note: 'Semua data pengajuan yang tampil.', cardBg: 'bg-gradient-to-br from-blue-200 to-blue-100', border: 'border-blue-200', label: 'text-blue-900', icon: FiClipboard, iconBg: 'bg-blue-900' },
            { title: 'Menunggu', value: totalMenunggu, note: 'Pengajuan berstatus pending.', cardBg: 'bg-gradient-to-br from-amber-200 to-orange-100', border: 'border-amber-200', label: 'text-amber-900', icon: FiClock, iconBg: 'bg-amber-900' },
            { title: 'Disetujui', value: totalDisetujui, note: 'Pengajuan yang telah disetujui.', cardBg: 'bg-gradient-to-br from-emerald-200 to-emerald-100', border: 'border-emerald-200', label: 'text-emerald-900', icon: FiCheckCircle, iconBg: 'bg-emerald-900' },
            { title: 'Selesai', value: totalSelesai, note: 'Pengajuan yang sudah selesai.', cardBg: 'bg-gradient-to-br from-indigo-200 to-indigo-100', border: 'border-indigo-200', label: 'text-indigo-900', icon: FiCheckCircle, iconBg: 'bg-indigo-900' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.title} className={`group relative overflow-hidden rounded-2xl border ${card.border} ${card.cardBg} p-5 shadow-md transition hover:shadow-xl hover:-translate-y-0.5 duration-200`}>
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300 rounded-2xl" />
                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${card.label}`}>{card.title}</p>
                    <p className="mt-3 text-2xl font-extrabold text-black">{card.value}</p>
                    <p className={`mt-4 text-xs leading-5 ${card.label}`}>{card.note}</p>
                  </div>
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${card.iconBg} text-white shadow-sm flex-shrink-0`}>
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── TABEL PENGAJUAN ── */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 bg-gradient-to-r from-blue-100 to-blue-50 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Rekap Data</p>
              <h2 className="mt-0.5 text-base font-bold text-black">Daftar Pengajuan</h2>
              <p className="mt-0.5 text-xs text-blue-900">{filteredPengajuan.length} data ditampilkan</p>
            </div>
            <div className="relative w-full lg:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari nama, NIK, wilayah, program, atau status..."
                className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">No</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">ID Data</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Nama Pemohon</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">NIK</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Program</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Wilayah</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Nominal</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Status</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Tanggal</th>
                  <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-widest text-slate-500">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPengajuan.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                          <FiClipboard className="h-6 w-6 text-slate-300" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700">
                            {pengajuanList.length === 0 ? 'Belum ada data pengajuan.' : 'Tidak ada hasil pencarian yang sesuai.'}
                          </p>
                          <p className="mt-1 text-sm text-slate-400">Data akan tampil setelah pengajuan masuk sesuai scope admin.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPengajuan.map((item, index) => {
                    const statusData = resolveStatus(item?.status);

                    return (
                      <tr key={getDataPengajuanId(item) || `${index}-pengajuan`} className="transition-colors duration-150 hover:bg-blue-50/60">
                        <td className="px-5 py-4 text-sm font-medium text-slate-400">{index + 1}</td>
                        <td className="px-5 py-4 text-sm font-bold text-slate-800 font-mono">{getDataPengajuanId(item) || '-'}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {(item?.namaLengkap || 'P').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-semibold text-slate-800">{item?.namaLengkap || '-'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-500 font-mono">{item?.nik || '-'}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{getProgramName(item)}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">
                          {[item?.kecamatan, item?.kabupatenKota].filter(Boolean).join(', ') || '-'}
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm font-bold text-slate-900">{formatCurrency(item?.nominalPengajuan)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${colorMap[statusData.color]}`}>
                            {statusData.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-500">{formatDate(item?.createdAt || item?.created_at)}</td>
                        <td className="px-5 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleOpenDetail(item)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-900 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-blue-800 active:scale-95"
                          >
                            <FiEye className="h-3.5 w-3.5" />
                            Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {detailModalOpen && selectedPengajuan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm" onClick={handleCloseDetailModal}>
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-slate-50 shadow-2xl" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="sticky top-0 z-10 overflow-hidden rounded-t-2xl">
              <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-900 flex items-center justify-center shadow-md flex-shrink-0">
                    <FiClipboard className="text-white h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Detail Pengajuan</p>
                    <h2 className="mt-0.5 text-xl font-bold text-slate-900">{selectedPengajuan.namaLengkap || '-'}</h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      ID {getDataPengajuanId(selectedPengajuan) || '-'} &bull; {getProgramName(selectedPengajuan)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseDetailModal}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 flex-shrink-0"
                >
                  <FiX className="h-3.5 w-3.5" />
                  Tutup
                </button>
              </div>
            </div>

            <div className="space-y-5 p-6">
              {/* Data Pemohon */}
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white px-5 py-4">
                  <h3 className="text-sm font-bold text-slate-900">Data Pemohon</h3>
                </div>
                <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="NIK" value={selectedPengajuan.nik} />
                  <DetailItem label="No KK" value={selectedPengajuan.noKk} />
                  <DetailItem label="Jenis Kelamin" value={selectedPengajuan.jenisKelamin} />
                  <DetailItem label="TTL" value={`${selectedPengajuan.tempatLahir || '-'}, ${formatDate(selectedPengajuan.tanggalLahir)}`} />
                  <DetailItem label="Agama" value={selectedPengajuan.agama} />
                  <DetailItem label="Status Perkawinan" value={selectedPengajuan.statusPerkawinan} />
                  <DetailItem label="Pekerjaan" value={selectedPengajuan.pekerjaan} />
                  <DetailItem label="Pendidikan" value={selectedPengajuan.pendidikanTerakhir} />
                  <DetailItem label="No HP" value={selectedPengajuan.noHp} />
                  <DetailItem label="Status Rumah" value={selectedPengajuan.statusRumah} />
                  <DetailItem label="Tanggungan" value={selectedPengajuan.jumlahTanggungan} />
                  <DetailItem label="Penghasilan" value={formatCurrency(selectedPengajuan.penghasilanPerBulan)} />
                </div>
              </section>

              {/* Alamat dan Pengajuan */}
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white px-5 py-4">
                  <h3 className="text-sm font-bold text-slate-900">Alamat dan Pengajuan</h3>
                </div>
                <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="Alamat" value={selectedPengajuan.alamat} />
                  <DetailItem label="RT/RW" value={`${selectedPengajuan.rt || '-'}/${selectedPengajuan.rw || '-'}`} />
                  <DetailItem label="Desa/Kelurahan" value={selectedPengajuan.desaKelurahan} />
                  <DetailItem label="Kecamatan" value={selectedPengajuan.kecamatan} />
                  <DetailItem label="Kab/Kota" value={selectedPengajuan.kabupatenKota} />
                  <DetailItem label="Provinsi" value={selectedPengajuan.provinsi} />
                  <DetailItem label="Kode Pos" value={selectedPengajuan.kodePos} />
                  <DetailItem label="Nominal Pengajuan" value={formatCurrency(selectedPengajuan.nominalPengajuan)} />
                  <DetailItem label="Via Penyaluran" value={selectedPengajuan.viaPenyaluran} />
                  <DetailItem label="No Tujuan" value={selectedPengajuan.noTujuan} />
                  <DetailItem label="Sumber Penghasilan" value={selectedPengajuan.sumberPenghasilan} />
                  <DetailItem label="Tanggal Input" value={formatDate(selectedPengajuan.createdAt || selectedPengajuan.created_at)} />
                </div>
              </section>

              {/* Riwayat Pengajuan */}
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white px-5 py-4">
                  <h3 className="text-sm font-bold text-slate-900">Riwayat Pengajuan</h3>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                    {selectedDetail.riwayat.length} riwayat
                  </span>
                </div>
                {selectedDetail.riwayat.length === 0 ? (
                  <p className="m-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                    Belum ada riwayat untuk data ini.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">ID Riwayat</th>
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Program</th>
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Lembaga</th>
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Sumber</th>
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Status</th>
                          <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Tanggal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedDetail.riwayat.map((item) => {
                          const statusData = resolveStatus(item?.status);
                          return (
                            <tr key={item?.id_pengajuan} className="transition-colors duration-150 hover:bg-slate-50/80">
                              <td className="px-5 py-3.5 text-sm font-mono text-slate-700">{item?.id_pengajuan || '-'}</td>
                              <td className="px-5 py-3.5 text-sm text-slate-700">{getProgramName(item)}</td>
                              <td className="px-5 py-3.5 text-sm text-slate-700">{getLembagaName(item)}</td>
                              <td className="px-5 py-3.5 text-sm text-slate-700">{item?.sumber || '-'}</td>
                              <td className="px-5 py-3.5 text-sm">
                                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${colorMap[statusData.color]}`}>
                                  {statusData.label}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-sm text-slate-500">{formatDate(item?.created_at || item?.createdAt)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <MediaGrid
                title="Dokumen Pengajuan"
                items={selectedDetail.dokumen}
                getTitle={(item) => item?.jenisDokumen?.nama || item?.nama_jenis_dokumen || `Dokumen #${item?.id_dokumen || '-'}`}
                getSubtitle={(item) => item?.jenisDokumen?.keterangan || `Diunggah ${formatDate(item?.uploaded_at)}`}
              />

              <MediaGrid
                title="Foto Survey"
                items={selectedDetail.foto}
                getTitle={(item) => {
                  const jenisFoto = item?.jenisFoto || jenisFotoById.get(normalizeId(item?.id_jenis_foto));
                  return jenisFoto?.nama || `Foto #${item?.id_foto || '-'}`;
                }}
                getSubtitle={(item) => {
                  const jenisFoto = item?.jenisFoto || jenisFotoById.get(normalizeId(item?.id_jenis_foto));
                  return jenisFoto?.keterangan || `Status valid: ${item?.is_valid ? 'Ya' : 'Tidak'}`;
                }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ListPengajuanAdmin;
